import { action, makeObservable, observable } from "mobx";
import type {
  GridFilterModel,
  GridRowId,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import type { Emitter } from "nanoevents";
import { createNanoEvents } from "nanoevents";

type EditCell<R extends GridValidRowModel = GridValidRowModel> = {
  field: string;
  row: R;
  error?: string;
};

type EditRow<R extends GridValidRowModel = GridValidRowModel> = {
  id: GridRowId;
  row: R;
};

type DataGridStoreEvents<R extends GridValidRowModel = GridValidRowModel> = {
  rowEditStop: (row: R) => void;
  rowEditStart: (row: R) => void;
  cellEditStop: (row: R) => void;
  cellEditStart: (row: R) => void;

  appliedFilterModelChanged: (appliedFilters: GridFilterModel) => void;
};
export class DataGridStore<R extends GridValidRowModel = GridValidRowModel> {
  @observable
  editRow?: EditRow<R>;
  editCell?: EditCell<R>;

  @observable
  appliedFilterModel: GridFilterModel = {
    items: [],
  };

  emitter: Emitter<DataGridStoreEvents>;

  constructor() {
    makeObservable(this);
    this.emitter = createNanoEvents<DataGridStoreEvents>();
  }
  @action.bound
  startRowEditMode(params: EditRow<R>) {
    this.editRow = params;
    this.emitter.emit("rowEditStart", params);
  }

  @action.bound
  stopRowEditMode() {
    const row = { ...this.editRow };
    this.editRow = undefined;
    this.emitter.emit("rowEditStop", row);
  }

  @action.bound
  startCellEditMode(params: EditCell<R>) {
    this.editCell = params;
    this.emitter.emit("cellEditStart", params);
  }

  @action.bound
  stopCellEditMode() {
    const cell = { ...this.editCell };
    this.editCell = undefined;
    this.emitter.emit("cellEditStop", cell);
  }

  @action.bound
  setAppliedFilterModel(
    filterModel: GridFilterModel,
    emitEvents: boolean = true,
  ) {
    if (emitEvents) {
      this.emitter.emit("appliedFilterModelChanged", filterModel);
    }
    this.appliedFilterModel = filterModel;
  }

  @action.bound
  setEditCellValue(params: { id: GridRowId; field: string; value: any }) {
    const { id, field, value } = params;
    const editRow = this.editRow;
    if (editRow) {
      this.editRow = {
        ...editRow,
        id,
        row: {
          ...editRow.row,
          [field]: value,
        },
      };
    }
    const editCell = this.editCell;
    if (editCell) {
      this.editCell = {
        ...editCell,
        row: {
          ...editCell.row,
          [field]: value,
        },
      };
    }
  }

  isRowEditing(id: GridRowId) {
    return this.editRow?.id === id;
  }

  getEditCellValue(id: GridRowId, field: string) {
    return this.editCell?.row?.[field];
  }

  getEditCell() {
    return this.editCell;
  }
}
